/**
 * Team Profiles Store
 *
 * Live list of all TeamProfile docs for the currently signed-in vendor.
 * Backed by a Firestore subscription on serviceProviders/{vendorId}/profiles.
 *
 * Lifecycle:
 *  - On vendor sign-in: call subscribe(vendorId).
 *  - On signout / vendor switch: call unsubscribe().
 *  - Components read `profiles` directly. Mutations go through
 *    teamProfileService (which writes to Firestore; the listener echoes back).
 *
 * NOT persisted — Firestore is the source of truth and the list is small.
 */

import { create } from "zustand";

import { subscribeToProfiles } from "../services/teamProfileService";
import { useActiveProfileStore } from "./activeProfileStore";
import { isSwapNoise } from "./authSwapStore";
import type { TeamProfile } from "../types/teamProfileTypes";

interface TeamProfilesState {
  profiles: TeamProfile[];
  isLoading: boolean;
  /** vendorId we're currently subscribed for, or null. */
  subscribedVendorId: string | null;
  /** unsubscribe handle from the current Firestore listener, or null. */
  unsubscribeFn: (() => void) | null;

  subscribe: (vendorId: string) => void;
  unsubscribe: () => void;
}

export const useTeamProfilesStore = create<TeamProfilesState>()((set, get) => ({
  profiles: [],
  isLoading: false,
  subscribedVendorId: null,
  unsubscribeFn: null,

  subscribe: (vendorId) => {
    const { subscribedVendorId, unsubscribeFn } = get();
    // If we're already subscribed to this vendor, no-op.
    if (subscribedVendorId === vendorId && unsubscribeFn) return;
    // If switching vendors, tear down the previous listener first.
    if (unsubscribeFn) unsubscribeFn();

    set({ isLoading: true, subscribedVendorId: vendorId });

    const unsub = subscribeToProfiles(
      vendorId,
      (profiles) => {
        set({ profiles, isLoading: false });
        // Solo-vendor passthrough: if exactly one profile exists and nothing
        // VALID for THIS vendor is active on the device yet, auto-select it.
        // Multi-profile vendors go through the picker which sets the active
        // profile explicitly. The vendor-scoped validity check matters after
        // an enterVenue() swap: a stale pick from the previous vendor must not
        // block auto-select in the new single-profile venue.
        if (profiles.length === 1) {
          const { activeProfileId, vendorId: activeVendorId, setActiveProfile } =
            useActiveProfileStore.getState();
          const validForThisVendor =
            !!activeProfileId &&
            (!activeVendorId || activeVendorId === vendorId) &&
            profiles.some((p) => p.id === activeProfileId);
          if (!validForThisVendor) setActiveProfile(profiles[0].id, vendorId);
        }
      },
      (err) => {
        // Swallow transient permission-denied. Two legitimate cases:
        //   1. Swap-window noise during signInWithCustomToken (caught by
        //      isSwapNoise while the swap flag is set).
        //   2. RN cold-launch race — the React Native Firestore SDK fires
        //      this listener once with denied before its restored auth
        //      state propagates into the listener context, then retries
        //      silently and the second callback succeeds. The error here
        //      is harmless; logging it pops the RN dev overlay.
        // The listener auto-recovers in both cases. Other error codes
        // still surface to logs for debugging.
        const code = (err as { code?: string } | undefined)?.code;
        if (code === "permission-denied" || isSwapNoise(err)) {
          set({ isLoading: false });
          return;
        }
        console.error("[teamProfilesStore] subscription error:", err);
        set({ isLoading: false });
      }
    );
    set({ unsubscribeFn: unsub });
  },

  unsubscribe: () => {
    const { unsubscribeFn } = get();
    if (unsubscribeFn) unsubscribeFn();
    set({
      profiles: [],
      isLoading: false,
      subscribedVendorId: null,
      unsubscribeFn: null,
    });
  },
}));
